// src/components/CreatorDashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, getDocs, doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import './CreatorDashboard.css';

const greetings = [
  'Ciao', 'Ayo', 'Howdy', 'Coucou', 'Wagwan',
  'Bing Bong', "C'kemi", 'Hola', 'Salut', 'Oi',
  'Hej', 'Servus', 'Hallo', "G'day", 'Marhaba',
  'Ahlan', 'Privet', 'Salaam', 'Aloha', 'Konnichiwa',
];

export default function CreatorDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [creatorData, setCreatorData] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [randomGreeting, setRandomGreeting] = useState('');

  // Preview card state
  const [showCardPreview, setShowCardPreview] = useState(false);
  const [previewRoute, setPreviewRoute] = useState(null);
  const [isPreviewFlipped, setIsPreviewFlipped] = useState(false);
  const [showBackContent, setShowBackContent] = useState(false);

  useEffect(() => {
    loadCreatorData();
  }, []);

  useEffect(() => {
    const idx = Math.floor(Math.random() * greetings.length);
    setRandomGreeting(greetings[idx]);
  }, []);

  const loadCreatorData = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      // Load creator profile from users collection
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists() && userDoc.data().isCreator) {
        const userData = userDoc.data();
        // Flatten creator profile data for easier access
        const flattenedData = {
          ...userData,
          ...userData.creatorProfile,
          // Default to 1 route allowed if not specified
          maxRoutes: userData.maxRoutes || 1
        };
        setCreatorData(flattenedData);
      }

      // Load all routes created by this user
      const submissionsQuery = query(
        collection(db, 'routes'),
        where('creatorId', '==', user.uid),
        orderBy('updatedAt', 'desc')
      );

      const submissionsSnapshot = await getDocs(submissionsQuery);
      const submissionsData = submissionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setSubmissions(submissionsData);
    } catch (error) {
      console.error('Error loading creator data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    // Check if user has reached their route limit
    const maxRoutes = creatorData?.maxRoutes || 1;
    if (submissions.length >= maxRoutes) {
      alert(`You've reached your route limit (${maxRoutes} route${maxRoutes > 1 ? 's' : ''}). Contact support if you'd like to create more routes.`);
      return;
    }
    navigate('/creator-submit');
  };

  // Check if creator can create new routes
  const canCreateNewRoute = () => {
    const maxRoutes = creatorData?.maxRoutes || 1;
    return submissions.length < maxRoutes;
  };

  const handleEdit = (submissionId) => {
    navigate(`/creator-submit/${submissionId}`);
  };

  const getStatusInfo = (creatorStatus) => {
    const statusConfig = {
      draft: { color: '#666', label: 'Draft Saved', description: 'Working draft - not yet submitted' },
      submitted: { color: '#3b82f6', label: 'Submitted for Review', description: 'Awaiting admin review' },
      needs_attention: { color: '#f59e0b', label: 'Needs Attention', description: 'Requires updates before approval' },
      live: { color: '#10b981', label: 'Live', description: 'Available in the store' },
      off_store: { color: '#ef4444', label: 'Off Store', description: 'Not currently available for purchase' }
    };

    return statusConfig[creatorStatus] || statusConfig.draft;
  };

  const handleViewPerformance = (submissionId) => {
    navigate(`/route-performance/${submissionId}`);
  };

  // Handle card preview
  const handlePreviewCard = (route) => {
    setPreviewRoute(route);
    setShowCardPreview(true);
    setIsPreviewFlipped(false);
    setShowBackContent(false);
  };

  const handleClosePreview = () => {
    setShowCardPreview(false);
    setPreviewRoute(null);
    setIsPreviewFlipped(false);
    setShowBackContent(false);
  };

  // Handle preview flip with delay for content on both sides
  const handlePreviewFlip = () => {
    if (!isPreviewFlipped) {
      // Flipping to back - start CSS animation, show back content at 160ms
      setIsPreviewFlipped(true);
      setTimeout(() => setShowBackContent(true), 160);
    } else {
      // Flipping to front - start CSS animation, show front content at 160ms
      setIsPreviewFlipped(false);
      setTimeout(() => setShowBackContent(false), 160);
    }
  };

  if (loading) {
    return (
      <div className="creator-dashboard-loading">
        <div className="loading-spinner">⏳</div>
        <p>Loading your dashboard...</p>
      </div>
    );
  }

  return (
    <div className="creator-dashboard-container">
      <div className="creator-dashboard-content">
        {/* Header */}
        <div className="dashboard-header">
          <div className="header-buttons-row">
            <button
              className="btn-logout"
              onClick={() => {
                auth.signOut();
                navigate('/');
              }}
            >
              Log Out
            </button>
            <button
              className="btn-edit-profile-header"
              onClick={() => navigate('/creator-info')}
            >
              Edit Profile
            </button>
          </div>
          <div className="header-text">
            <h1 className="dashboard-title">
              {randomGreeting || 'Welcome'}, {creatorData?.firstName || 'Creator'}!
            </h1>
            <p className="dashboard-subtitle">
              Manage your route submissions and track their status
            </p>
          </div>
        </div>

        {/* Routes List */}
        <div className="routes-section">
          {submissions.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🗺️</div>
              <h3>No routes yet</h3>
              <p>Create your first route to get started!</p>
              <button
                className="btn-create-new"
                onClick={handleCreateNew}
                disabled={!canCreateNewRoute()}
                style={{ opacity: canCreateNewRoute() ? 1 : 0.5 }}
              >
                Create Your First Route
              </button>
            </div>
          ) : (
            <div className="routes-list">
              {submissions.map((submission) => {
                const statusInfo = getStatusInfo(submission.creatorStatus || 'draft');
                return (
                  <div key={submission.id} className="route-list-item">
                    <h3 className="route-list-name">
                      {submission.name || submission.routeName || 'Untitled Route'}
                    </h3>
                    <div className="route-list-status">
                      <div className="status-text">
                        <span className="status-label">Creator Status:</span>
                        <strong>{statusInfo.label}</strong>
                      </div>
                    </div>
                    {submission.adminNotes && (
                      <div className="admin-notes-inline">
                        <strong>Admin Notes:</strong> {submission.adminNotes}
                      </div>
                    )}
                    <div className="route-list-actions">
                      <button
                        className="btn-edit-route"
                        onClick={() => handleEdit(submission.id)}
                      >
                        Edit Route
                      </button>
                      <button
                        className="btn-preview-card"
                        onClick={() => handlePreviewCard(submission)}
                      >
                        Preview Card
                      </button>
                      <button
                        className="btn-view-performance"
                        onClick={() => handleViewPerformance(submission.id)}
                      >
                        View Stats
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Create New Route Button */}
        {submissions.length > 0 && (
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            {canCreateNewRoute() ? (
              <button
                className="btn-create-new"
                onClick={handleCreateNew}
              >
                + Create New Route
              </button>
            ) : (
              <p style={{ color: '#f59e0b', fontSize: '0.9rem' }}>
                You've reached your route limit. Contact support to create more routes.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Card Preview Modal */}
      {showCardPreview && previewRoute && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}
          onClick={handleClosePreview}
        >
          <div
            style={{
              backgroundColor: '#0a0a0a',
              padding: '30px',
              borderRadius: '12px',
              maxHeight: '90vh',
              overflowY: 'auto',
              width: '90%',
              maxWidth: '450px',
              textAlign: 'center',
              boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ color: '#FFD700', marginBottom: '10px', fontSize: '1.5rem' }}>
              Store Card Preview
            </h3>
            <p style={{ color: '#999', marginBottom: '20px', fontSize: '0.9rem' }}>
              This is how your route will appear in the store
            </p>

            <style>{`
              @import url('https://fonts.googleapis.com/css2?family=Caveat:wght@400;500;600;700&display=swap');

              @font-face {
                font-family: 'PassFont';
                src: url('/fonts/PassFont.ttf') format('truetype');
                font-weight: 400;
                font-style: normal;
                font-display: swap;
              }

              @font-face {
                font-family: 'MainFontLight';
                src: url('/fonts/MainFontLight.ttf') format('truetype');
                font-weight: 300;
                font-style: normal;
                font-display: swap;
              }

              .creator-preview-card-container {
                width: 300px;
                height: 485px;
                margin: 0 auto;
                perspective: 1000px;
              }

              .creator-preview-card {
                width: 100%;
                height: 100%;
                position: relative;
                transform-style: preserve-3d;
                -webkit-transform-style: preserve-3d;
                transition: transform 0.6s;
              }

              .creator-preview-card.flipped {
                transform: rotateY(180deg);
              }

              .creator-preview-card-face {
                position: absolute;
                width: 100%;
                height: 100%;
                backface-visibility: hidden;
                -webkit-backface-visibility: hidden;
                border-radius: 12px;
                padding: 20px;
                display: flex;
                flex-direction: column;
                cursor: pointer;
              }

              .creator-preview-card-front {
                align-items: center;
                justify-content: space-between;
              }

              .creator-preview-card-back {
                transform: rotateY(180deg);
                justify-content: space-between;
              }
            `}</style>

            <div className="creator-preview-card-container">
              <div className={`creator-preview-card ${isPreviewFlipped ? 'flipped' : ''}`}>
                {!showBackContent ? (
                  /* Front Face */
                  <div
                    className="creator-preview-card-face creator-preview-card-front"
                    onClick={handlePreviewFlip}
                    style={{
                      background: previewRoute.cardColors?.background || '#072231',
                      border: `2px solid ${previewRoute.cardColors?.borderColor || '#FFFFFF'}`
                    }}
                  >
                    {/* Beta Ribbon - Only on front */}
                    {previewRoute.storeStatus === 'beta' && (
                      <div style={{
                        position: 'absolute',
                        top: '-2px',
                        left: '-2px',
                        background: `linear-gradient(135deg, ${previewRoute.cardColors?.buttonGradientStart || '#FFD700'}, ${previewRoute.cardColors?.buttonGradientEnd || '#FFA500'})`,
                        padding: '8px 15px',
                        borderTopLeftRadius: '10px',
                        borderBottomRightRadius: '20px',
                        borderTop: `2px solid ${previewRoute.cardColors?.borderColor || '#FFFFFF'}`,
                        borderLeft: `2px solid ${previewRoute.cardColors?.borderColor || '#FFFFFF'}`,
                        color: previewRoute.cardColors?.buttonTextColor || '#072231',
                        fontSize: '0.85rem',
                        fontWeight: '700',
                        letterSpacing: '0.5px',
                        zIndex: 10,
                        boxShadow: '2px 2px 10px rgba(0, 0, 0, 0.3)'
                      }}>
                        INAUGURAL
                      </div>
                    )}

                    {/* Flip button - Only on front */}
                    <button style={{
                      position: 'absolute',
                      top: '15px',
                      right: '15px',
                      width: '40px',
                      height: '40px',
                      background: 'transparent',
                      border: 'none',
                      color: previewRoute.cardColors?.flipButtonColor || '#FFFFFF',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 10,
                      padding: 0
                    }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" fill="currentColor"/>
                      </svg>
                    </button>

                    <div style={{ textAlign: 'center', marginTop: '35px' }}>
                      {/* Title */}
                      <div style={{
                        color: previewRoute.cardColors?.titleColor || '#FFFFFF',
                        fontSize: '2.2rem',
                        fontWeight: '400',
                        marginBottom: '10px',
                        fontFamily: "'PassFont', sans-serif",
                        letterSpacing: '-0.5px',
                        lineHeight: '1.1'
                      }}>
                        {previewRoute.name || 'Route Title'}
                      </div>

                      {/* Tagline */}
                      <div style={{
                        color: previewRoute.cardColors?.taglineColor || '#FFD700',
                        fontSize: '0.85rem',
                        marginBottom: '20px',
                        fontFamily: "'MainFontLight', sans-serif",
                        fontWeight: '300'
                      }}>
                        {previewRoute.storeTagline || 'Your adventure awaits'}
                      </div>
                    </div>

                    {/* Image */}
                    <div style={{
                      height: '174px',
                      width: '174px',
                      background: previewRoute.storeCoverImage ? 'transparent' : 'rgba(0,0,0,0.2)',
                      borderRadius: '10px',
                      marginBottom: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      flexShrink: 0
                    }}>
                      {previewRoute.storeCoverImage ? (
                        <img
                          src={previewRoute.storeCoverImage}
                          alt="Route"
                          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                      ) : (
                        <span style={{ color: '#666', fontSize: '3rem' }}>🏔️</span>
                      )}
                    </div>

                    {/* Go Button */}
                    <button style={{
                      padding: '12px 0',
                      border: 'none',
                      borderRadius: '12px',
                      background: `linear-gradient(135deg, ${previewRoute.cardColors?.buttonGradientStart || '#FFD700'}, ${previewRoute.cardColors?.buttonGradientEnd || '#FFA500'})`,
                      color: previewRoute.cardColors?.buttonTextColor || '#072231',
                      fontWeight: '700',
                      fontSize: '1.6rem',
                      cursor: 'pointer',
                      boxShadow: '0 4px 15px rgba(255, 215, 0, 0.3)',
                      pointerEvents: 'none',
                      fontFamily: "'MainFontLight', sans-serif",
                      width: '33.33%'
                    }}>
                      Go
                    </button>
                  </div>
                ) : (
                  /* Back Face */
                  <div
                    className="creator-preview-card-face creator-preview-card-back"
                    onClick={handlePreviewFlip}
                    style={{
                      background: previewRoute.cardColors?.background || '#072231',
                      border: `2px solid ${previewRoute.cardColors?.borderColor || '#FFFFFF'}`
                    }}
                  >
                    {/* Flip button - back version */}
                    <button style={{
                      position: 'absolute',
                      top: '15px',
                      right: '15px',
                      width: '40px',
                      height: '40px',
                      background: 'transparent',
                      border: 'none',
                      color: previewRoute.cardColors?.flipButtonColor || '#FFFFFF',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 10,
                      padding: 0
                    }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" fill="currentColor"/>
                      </svg>
                    </button>

                    {/* Bullet points */}
                    <div style={{ marginTop: '50px', flex: 1, overflowY: 'auto', gap: '3px', marginBottom: '17px' }}>
                      {(previewRoute.storeBulletPoints?.length > 0 ? previewRoute.storeBulletPoints : [
                        { mainText: 'Amazing Feature', subText: 'Experience something extraordinary' },
                        { mainText: 'Unforgettable Journey', subText: 'Create memories that last forever' },
                        { mainText: 'Expert Guidance', subText: 'Professional support throughout' }
                      ]).map((point, index) => (
                        <div key={index} style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginBottom: '3px' }}>
                          <span style={{
                            color: previewRoute.cardColors?.bulletDiamondColor || '#FFD700',
                            fontSize: '1.04rem',
                            lineHeight: '2',
                            flexShrink: 0,
                            marginTop: '2px'
                          }}>◆</span>
                          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '-4px', textAlign: 'left' }}>
                            <div style={{
                              color: previewRoute.cardColors?.bulletMainTextColor || '#FFD700',
                              fontWeight: '600',
                              fontSize: '1.3rem',
                              lineHeight: '1.3',
                              fontFamily: "'Caveat', cursive"
                            }}>
                              {point.mainText || 'Feature'}
                            </div>
                            {point.subText && (
                              <div style={{
                                color: previewRoute.cardColors?.bulletSubTextColor || '#e8e8e8',
                                fontSize: '0.97rem',
                                lineHeight: '1',
                                paddingLeft: '2px'
                              }}>
                                {point.subText}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Go Button */}
                    <button style={{
                      padding: '12px 0',
                      border: 'none',
                      borderRadius: '12px',
                      background: `linear-gradient(135deg, ${previewRoute.cardColors?.buttonGradientStart || '#FFD700'}, ${previewRoute.cardColors?.buttonGradientEnd || '#FFA500'})`,
                      color: previewRoute.cardColors?.buttonTextColor || '#072231',
                      fontWeight: '700',
                      fontSize: '1.6rem',
                      cursor: 'pointer',
                      alignSelf: 'center',
                      boxShadow: '0 4px 15px rgba(255, 215, 0, 0.3)',
                      marginTop: 'auto',
                      marginBottom: '15px',
                      pointerEvents: 'none',
                      fontFamily: "'MainFontLight', sans-serif",
                      width: '33.33%'
                    }}>
                      Go
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div style={{
              textAlign: 'center',
              marginTop: '15px',
              color: '#999',
              fontSize: '0.85rem',
              fontStyle: 'italic'
            }}>
              Currently showing: {isPreviewFlipped ? 'Back Side' : 'Front Side'}
            </div>

            <button
              onClick={handleClosePreview}
              style={{
                marginTop: '20px',
                backgroundColor: '#853a38',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '500'
              }}
            >
              Close Preview
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
